/**
 * deployment-general-view.js
 */

import React from 'react';
import { Grid, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Types } from 'utils/constants';
import useRecordStore from 'contexts/record-store';
import IconSection from 'components/icon-section';
import InfoRow from 'components/info-row';
import StaticMap from 'components/static-map';


const useStyles = makeStyles(theme => ({
  locationContainer: {
    maxWidth: 320,
    [theme.size.menuDrawer.mediaRuleMobileContent]: {
      maxWidth: 'none'
    }
  },
  mapContainer: {
    display: 'flex', 
    [theme.size.menuDrawer.mediaRuleMobileContent]: {
      marginTop: theme.spacing(-3),
      marginLeft: theme.spacing(5),
      marginBottom: theme.spacing(3),
    }
  }
}));

 // Request fulfillment options
 const FulfillmentStateMap = {
  [Types.FulfillmentState.DogDeployed]: 'Dog Deployed',
  [Types.FulfillmentState.DogNotDeployed]: 'Dog Not Deployed',
  [Types.FulfillmentState.CancelledEnroute]: 'Cancelled Enroute',
 }

export default function DeploymentGeneralView(props) {
  const {
    deploymentId,
  } = props;
  const classes = useStyles();
  const { t } = useTranslation('general');
  const recordStore = useRecordStore();
  const deploymentRecord = recordStore.useRecord(Types.Record.DEPLOYMENT, deploymentId).ornull;
  const workMode = deploymentRecord && deploymentRecord.get().workMode;
  const workModeName = workMode && Types.WorkMode.getName(t, workMode);


  const generateAddress = (location) => {
    return location.address1 
    ? <>
      {location.locationName !== location.address1 && <>{location.address1} <br/></>}
      {`${location.city}, ${location.region}`} <br/>
      {location.postCode}
    </>
    : '';
  }


  const getReviewedStatus = () => {
    const reviewer = deploymentRecord.get()?.review?.reviewer?.name;
    const reviewStatus = deploymentRecord.get()?.review?.status
    return reviewStatus === Types.ReviewStatus.ACCEPTED
      ? `Yes, by ${reviewer}`
      : reviewStatus === Types.ReviewStatus.REJECTED
        ? `Rejected, by ${reviewer}`
        : "No";
  }

  return (
    <>
      {deploymentRecord &&
      <>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} className={classes.locationContainer}>

            {/* time */}
            <IconSection iconName='event'>
              <Typography variant="body1">
                {format(parseISO(deploymentRecord.get().date), 'MMM dd, yyyy')}
              </Typography>
              <Typography variant="body1">
                {format(parseISO(deploymentRecord.get().date), 'h:mm aaa')}
              </Typography>
            </IconSection>


            {/* location */}
            <IconSection iconName='location'>
              {deploymentRecord.get().location.locationName &&
              <Typography variant="body1" style={{fontWeight: 500}}>
                {deploymentRecord.get().location.locationName}
              </Typography>}
              {deploymentRecord.get().location.address1 &&
              <>
                <Typography>
                  {generateAddress(deploymentRecord.get().location)}
                </Typography>
              </>}
            </IconSection>

          </Grid>

          {/* map */}
          {deploymentRecord.get().location.address1 &&
          <Grid 
            item 
            xs={12} 
            sm={6} 
            className={classes.mapContainer}
          >
            <StaticMap
              latitude={deploymentRecord.get().location.latitude}
              longitude={deploymentRecord.get().location.longitude}
              location={deploymentRecord.get().location}
            />
          </Grid>}
        </Grid>


        {/* summary info */}
        <IconSection iconName='summary'>

          {/* case number */}
          <InfoRow title="Case" text={deploymentRecord.get().caseNumber} />

          {/* requesting unit */}
          {deploymentRecord.get().requestingUnit &&
          <InfoRow title="Requesting Agency" text={deploymentRecord.get().requestingUnit?.name} />}

          {/* tags */}
          {deploymentRecord.get().tags.length > 0 &&
          <InfoRow title="Tags" text={deploymentRecord.get().tags.map(tag => tag.name).join(', ')} />}

          {/* dog */}
          <InfoRow title="Dog" text={deploymentRecord.get().dog?.name} />

          {/* request fulfillment */}
          <InfoRow 
            title="Fulfillment State" 
            text={FulfillmentStateMap[deploymentRecord.get().fulfillmentState] || ''} 
          />

          {/* category / work mode */}
          {deploymentRecord.get().fulfillmentState !== Types.FulfillmentState.DogNotDeployed && 
           deploymentRecord.get().fulfillmentState !== Types.FulfillmentState.CancelledEnroute &&
          <InfoRow title="Type" text={workModeName} />}

          {/* report reviewed */}
          <InfoRow title='Report Reviewed' text={getReviewedStatus()} />

          {/* reason for deployment */}
          {deploymentRecord.get().workMode === Types.WorkMode.PATROL && deploymentRecord.get().deploymentReason &&
          <InfoRow title="Reason For Deployment" text={deploymentRecord.get().deploymentReason} />}

        </IconSection>

      </>}
    </>
  );
}